import React from "react";
import { useMemo, useRef } from "react";
import L from "leaflet";
import { useBookingsMapContext } from "../Context/BookingsMapContext";
import { useAuthContext } from "../../../../lib/context/Auth/AuthContext";
import {
  FloorChildrenType,
  useMarkerData,
} from "../../../Floors/lib/floorData";
import { floorPlanHelpers } from "../../../Floors/lib/floorPlanHelpers";
import { renderToStaticMarkup } from "react-dom/server";
import { getPoiIcon, PoiIconType } from "../../../Floors/lib/poiMarkersHelper";

import { UserLeafletMarker } from "../../../shared/UserLeafletMarker/UserLeafletMarker";
import { BusyTimeSlots } from "../../../../api/grpc/workplaces/ggevent/ggevent";
import { MarkerSettings } from "../../../Floors/lib/useMarkerData";
import {
  AmenitiesFilter,
  POI,
} from "../../../../api/grpc/workplaces/workplaces";

interface MarkerType {
  id: string;
  marker: L.Marker;
  isBooked: boolean;
  isAssigned: boolean;
  isBlocked: boolean;
  busySlot: BusyTimeSlots[];
  type: string;
  location: string;
  name: string;
  tags: string[];
  username: string;
  attachedUserPopup?: boolean;
  attendeeImage: string;
  avatar?: string;
  amenities?: AmenitiesFilter;
  numberOfSeats?: number;
  assigneAllowToFind?: boolean;
  assigneEmail?: string;
  bookingWindow: number;
}

const GREEN = "#01EC96";
const RED = "#EA1D25";

const OPACITY = 0.3;

export const useBookingsMarkers = (
  rooms: FloorChildrenType[],
  desks: FloorChildrenType[],
  settings?: MarkerSettings,
  floorPOI?: POI[]
) => {
  const { markerData, cirleData, polygonalData } = useMarkerData(
    rooms,
    desks,
    [],
    settings
  );
  const { user } = useAuthContext();
  const markersCacheRef = useRef<MarkerType[]>([]);
  const circleCacheRef = useRef<L.Circle[]>([]);
  const polygonCacheRef = useRef<L.Polygon[]>([]);
  const floorPOICacheRef = useRef<L.Marker[]>([]);

  const { searchedAttendees } = useBookingsMapContext();
  const { getFloorEntityLeafletIcon } = floorPlanHelpers();

  const checkAttachedDeskUser = (floorEntity: any): boolean => {
    if (
      (!floorEntity.isBooked && !floorEntity.isAssigned) ||
      !searchedAttendees?.length
    ) {
      return false;
    }

    const matchingAttendee = searchedAttendees.find(
      (attendee) =>
        attendee.email === floorEntity?.assigneEmail &&
        (floorEntity?.assigneAllowToFind ||
          floorEntity?.assigneEmail === user?.email)
    );

    return !!matchingAttendee;
  };

  const shapes = useMemo<{
    _circles: L.Circle[];
    _polygonal: L.Polygon[];
  }>(() => {
    // clear cached circles
    circleCacheRef.current.forEach((cachedCircle) => {
      cachedCircle.remove();
    });

    polygonCacheRef.current.forEach((cachedPolygon) => {
      cachedPolygon.remove();
    });

    const _circles = cirleData.flat().map((floorEntity: any) => {
      const newCircle: L.Circle = L.circle(floorEntity?.latlng, {
        color: floorEntity.isBooked ? RED : GREEN,
        fillColor: floorEntity.isBooked ? RED : GREEN,
        fillOpacity: OPACITY,
        radius: floorEntity?.radius,
        className: floorEntity.isBooked ? "booked" : "",
        attribution:
          floorEntity?.id +
          "-innerGoGetIDSplitter-" +
          floorEntity?.name +
          "-innerGoGetIDSplitter-" +
          (floorEntity?.labelAlignment || "center"),
      });

      return newCircle;
    });

    const _polygonal = polygonalData.flat().map((floorEntity: any) => {
      let newPolygon: L.Polygon | L.Rectangle;

      if (floorEntity.latlng[0]?.type === "polygon") {
        newPolygon = L.polygon(floorEntity?.latlng, {
          color: floorEntity.isBooked ? RED : GREEN,
          fillColor: floorEntity.isBooked ? RED : GREEN,
          fillOpacity: OPACITY,
          className: floorEntity.isBooked ? "booked" : "",
          attribution:
            floorEntity?.id +
            "-innerGoGetIDSplitter-" +
            floorEntity?.name +
            "-innerGoGetIDSplitter-" +
            (floorEntity?.labelAlignment || "center"),
        });
      } else {
        newPolygon = L.rectangle(floorEntity?.latlng, {
          color: floorEntity.isBooked ? RED : GREEN,
          fillColor: floorEntity.isBooked ? RED : GREEN,
          fillOpacity: OPACITY,
          className: floorEntity.isBooked ? "booked" : "",
          attribution:
            floorEntity?.id +
            "-innerGoGetIDSplitter-" +
            floorEntity?.name +
            "-innerGoGetIDSplitter-" +
            (floorEntity?.labelAlignment || "center"),
        });
      }

      return newPolygon;
    });

    circleCacheRef.current = _circles;
    polygonCacheRef.current = _polygonal;

    return { _circles, _polygonal };
  }, [rooms, desks]);

  const poiMarkers = useMemo<L.Marker[]>(() => {
    floorPOICacheRef.current.forEach((cachedMarker) => {
      cachedMarker.remove();
    });

    if (!floorPOI) return [];

    const _poiMarkers = floorPOI?.map((poi) => {
      const newMarker: L.Marker = L.marker(
        [poi.marker?.latitude || 0, poi.marker?.longitude || 0],
        {
          icon: getPoiIcon(poi.type as PoiIconType),
          draggable: false,
          attribution: poi?.id,
          alt: poi?.type,
        }
      );

      return newMarker;
    });

    floorPOICacheRef.current = _poiMarkers;

    return _poiMarkers;
  }, [floorPOI]);

  const markers = useMemo<MarkerType[]>(() => {
    markersCacheRef.current.forEach((cachedMarker) => {
      cachedMarker.marker.remove();
    });

    const _markers = markerData.flat().map((floorEntity: any) => {
      return {
        id: floorEntity.id,
        marker: L.marker(floorEntity?.latlng, {
          icon: getFloorEntityLeafletIcon(floorEntity, "bookings"),
          draggable: false,
          attribution: floorEntity?.id,
          alt: "Marker",
        }),
        isBooked: floorEntity.isBooked,
        isAssigned: floorEntity.isAssigned,
        isBlocked: floorEntity.isBlocked,
        busySlot: floorEntity.busySlots,
        type: floorEntity.type,
        location: floorEntity?.location,
        name: floorEntity.name,
        tags: floorEntity.tags,
        username: floorEntity?.username,
        avatar: floorEntity?.avatar,
        attachedUserPopup:
          floorEntity?.type === "Room"
            ? false
            : checkAttachedDeskUser(floorEntity),
        amenities: floorEntity?.amenities || [],
        numberOfSeats: floorEntity?.numberOfSeats,
        assigneAllowToFind: floorEntity?.assigneAllowToFind,
        assigneEmail: floorEntity?.assigneEmail,
        attendeeImage: "",
        bookingWindow: floorEntity?.bookingWindow ?? 180,
      };
    });

    // we add extra markers for when a user from Find people matches a
    // reserved desk
    if (searchedAttendees.length > 0 && desks.length > 0) {
      searchedAttendees.map((attendee) =>
        desks
          .filter(
            (desk) =>
              (desk.isBooked || desk.isAssigned) &&
              desk.organizer?.email === attendee.email
          )
          .map((deskMarker: FloorChildrenType) => {
            return _markers.push({
              busySlot: deskMarker.busySlots,
              id: deskMarker.id,
              isBooked: deskMarker.isBooked,
              isAssigned: deskMarker.isAssigned,
              isBlocked: deskMarker.isBlocked,
              location: "",
              name: deskMarker.name,
              tags: deskMarker.tags,
              type: "Desk",
              username: deskMarker.organizer?.displayName,
              avatar: attendee?.avatar?.url || "",
              attachedUserPopup: true,
              attendeeImage: attendee?.avatar?.url || "",
              amenities: [],
              numberOfSeats: 0,
              assigneAllowToFind: deskMarker.organizer?.allowToFind,
              assigneEmail: deskMarker.organizer?.email,
              bookingWindow: deskMarker.bookingWindow ?? 180,
              marker: L.marker(
                [
                  deskMarker?.marker?.latitude || 0,
                  deskMarker?.marker?.longitude || 0,
                ],
                {
                  icon: new L.DivIcon({
                    html: renderToStaticMarkup(
                      <UserLeafletMarker
                        key={deskMarker.id}
                        attendee={attendee}
                        showReservationInfo={false}
                      />
                    ),
                    iconSize: [0, 0],
                    iconAnchor: [0, 0],
                  }),
                  draggable: false,
                  attribution: deskMarker.id,
                  alt: "User marker",
                }
              ),
            });
          })
      );
    }

    markersCacheRef.current = _markers;

    return _markers;
  }, [rooms, desks, searchedAttendees]);

  const openMarkerOnInitialization = (id: string) => {
    let clickedMarker = markers.filter(
      ({ marker }) => marker.options.attribution === id
    );

    if (clickedMarker.length >= 1) {
      clickedMarker[0].marker.openPopup();
    }
  };

  return { markers, shapes, poiMarkers, openMarkerOnInitialization };
};
